import { Md5 } from 'ts-md5';
import { Downloader, warp_fx, video_engine } from '@picsart/video-sdk';
import PQueue from 'p-queue';

import type { IWfxEffect } from 'pages/Batch/sections/Sidebar/WfxTool/constants';
import type { IProcessResult } from './toolchain';

export interface WfxProcessAttrs {
  effect?: IWfxEffect | null;
  params?: Record<string, number>;
}

const EFFECTS_BASE_URL = 'https://effres.picsart.com/warp-effects/effects/';
const RESOURCES_BASE_URL = 'https://effres.picsart.com/warp-effects/res/';

const wfxQueue = new PQueue({ concurrency: 1 });

function generateEffectUrl(effectId: string): string {
  return `${EFFECTS_BASE_URL}${Md5.hashStr(effectId)}.json`;
}

function generateResourceUrl(fileName: string): string {
  const ext = fileName.match(/(?:\.([^.]+))?$/)?.[0] || '';
  const baseName = fileName.replace(ext, '');
  return `${RESOURCES_BASE_URL}${Md5.hashStr(baseName)}${ext}`;
}

// Cache effect data to avoid re-fetching
const effectDataCache = new Map<string, any>();

async function fetchEffectData(effectId: string): Promise<any> {
  if (effectDataCache.has(effectId)) {
    return effectDataCache.get(effectId);
  }
  const url = generateEffectUrl(effectId);
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch effect ${effectId}: ${response.status}`);
  const data = await response.json();
  effectDataCache.set(effectId, data);
  return data;
}

async function createEffectDescriptor(effectId: string): Promise<warp_fx.core.BuilderDescriptor> {
  const data = await fetchEffectData(effectId);
  const dataString = typeof data === 'string' ? data : JSON.stringify(data);

  const factory = warp_fx.core.BuilderDescriptorFactory.factory();
  const builderDescriptor = factory.createBuilderDescriptorFromData(dataString, '', effectId);

  // Load required resources (LUTs, textures, etc.)
  const { requiredResources } = builderDescriptor;
  if (requiredResources?.length > 0) {
    const downloader = new Downloader();
    const resources = await Promise.all(
      requiredResources.map(async (rd: warp_fx.resources.FileResourceDescriptor | warp_fx.resources.ImageResourceDescriptor) => {
        const url = generateResourceUrl(rd.fileName);
        const path = await downloader.download(url);
        if (rd instanceof warp_fx.resources.FileResourceDescriptor) {
          return warp_fx.resources.FileReaderResource.create(path, rd);
        }
        return warp_fx.resources.ImageFileResource.create(path, rd);
      }),
    );
    builderDescriptor.setResources(resources);
  }

  return builderDescriptor;
}

async function renderWfx(imageUrl: string, width: number, height: number, effectId?: string | null, params?: Record<string, number>): Promise<HTMLCanvasElement> {
  const offscreen = new OffscreenCanvas(width, height);

  const project = video_engine.project.Project.create();
  const resource = video_engine.resources.PhotoResource.create(imageUrl);
  const photoLayer = video_engine.layers.PhotoLayer.create(resource);
  photoLayer.desiredDuration = 1;
  photoLayer.desiredResolution = [width, height];

  const composition = video_engine.project.Composition.create();
  composition.desiredResolution = [width, height];
  composition.addLayer(photoLayer);
  project.active = composition;

  // Apply effect if selected
  if (effectId) {
    const descriptor = await createEffectDescriptor(effectId);
    const effectResource = video_engine.resources.EffectResource.create(descriptor);
    const effectComponent = video_engine.components.EffectComponent.create(effectResource, `wfx_effect__${effectId}`);
    photoLayer.addComponent(effectComponent);

    if (params) {
      Object.entries(params).forEach(([name, value]) => {
        try {
          effectComponent.property(name).value = String(value);
        } catch {
          // parameter may not exist
        }
      });
    }
  }

  const frameRenderer = video_engine.media.FrameRenderer.create(project);

  try {
    frameRenderer.setPreviewCanvas(offscreen);
    await frameRenderer.drawFrame(0);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(offscreen, 0, 0);
    return canvas;
  } finally {
    frameRenderer.delete();
  }
}

export function cancelWfxQueue() {
  wfxQueue.clear();
}

export function getWfxProcess({ effect, params }: WfxProcessAttrs) {
  return async (image: HTMLImageElement, isCancel: () => boolean): Promise<IProcessResult> => {
    const result = await wfxQueue.add(
      async () => {
        if (isCancel()) throw new Error('cancelled');

        const canvas = await renderWfx(image.src, image.naturalWidth, image.naturalHeight, effect?.id, params);

        const blob = await new Promise<Blob>(res => {
          canvas.toBlob(b => res(b!), 'image/webp');
        });
        return { blob, result: URL.createObjectURL(blob) };
      },
      { throwOnTimeout: true },
    );

    return result as IProcessResult;
  };
}
